import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supportSessionApi } from '@formosa/shared/api/im';
import {
  abandonWaitingSupportSession,
  canMemberSendInSupport,
  isTerminalSupportStatus,
  startOrResumeSupportSession,
} from '@formosa/shared/utils/customerServiceEntry';
import MessageTimeline from '../components/chat/MessageTimeline';
import Composer from '../components/chat/Composer';
import TypingIndicator from '../components/chat/TypingIndicator';
import PinnedMessagesBar from '../components/chat/PinnedMessagesBar';
import ConversationMuteMenu from '../components/chat/ConversationMuteMenu';
import SupportSessionStatusBar from '../pages/chat/components/SupportSessionStatusBar';
import SupportSessionHistoryPanel from '../pages/chat/components/SupportSessionHistoryPanel';
import { resolveTitle } from '../components/chat/ConversationList';
import { useChat } from '../chat/ChatProvider';
import { normalizeMuteMode, shouldAlertUnread } from '../chat/muteHelpers';
import { useMiniChat } from './MiniChatProvider';
import '../styles/supportChat.css';

const SOCKET_LABEL = {
  open: '已連線',
  connecting: '連線中',
  reconnecting: '重連中',
  disconnected: '已斷線',
  idle: '未連線',
};

export default function MiniChatWindow({
  conversationId,
  minimized,
  support = false,
  supportSessionId = null,
  supportStatus = null,
}) {
  const navigate = useNavigate();
  const { state, myMemberId, openConversation, dispatch } = useChat();
  const { closeMiniChat, minimizeMiniChat, restoreMiniChat } = useMiniChat();
  const cid = String(conversationId);

  const [session, setSession] = useState(
    supportSessionId
      ? { sessionId: supportSessionId, status: supportStatus, conversationId: cid }
      : null
  );
  const [busy, setBusy] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyItems, setHistoryItems] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState('');
  const [selectedHistoryId, setSelectedHistoryId] = useState(null);
  const sessionRef = useRef(session);
  sessionRef.current = session;

  const loadHistory = useCallback(async () => {
    if (!support) return;
    setHistoryLoading(true);
    setHistoryError('');
    try {
      const page = await supportSessionApi.history({ limit: 20 });
      setHistoryItems(page?.items || []);
    } catch (err) {
      setHistoryError(err?.message || '無法載入歷史');
      setHistoryItems([]);
    } finally {
      setHistoryLoading(false);
    }
  }, [support]);

  const refreshSession = useCallback(async () => {
    if (!support) return;
    try {
      const current = await supportSessionApi.current();
      if (current?.sessionId) {
        setSession(current);
      }
    } catch {
      /* ignore */
    }
  }, [support]);

  useEffect(() => {
    if (!support) return undefined;
    refreshSession();
    loadHistory();
    const timer = setInterval(refreshSession, 8000);
    return () => clearInterval(timer);
  }, [support, refreshSession, loadHistory]);

  const conv = state.conversations.find(
    (c) => String(c.conversationId || c.id) === cid
  );
  const title = support
    ? '線上客服'
    : conv
      ? resolveTitle(conv, state.participantSummaries, myMemberId)
      : `會話 ${cid}`;
  const unread = conv?.unreadCount || 0;
  const socketLabel = SOCKET_LABEL[state.socketStatus] || state.socketStatus;
  const allowComposer = !support || canMemberSendInSupport(session?.status);

  async function handleLeave() {
    if (!session?.sessionId) return;
    setBusy(true);
    try {
      const resp = await supportSessionApi.leave(session.sessionId, {
        reason: 'CUSTOMER_CANCELLED',
      });
      setSession(resp);
      loadHistory();
    } finally {
      setBusy(false);
    }
  }

  async function handleEnd() {
    if (!session?.sessionId) return;
    setBusy(true);
    try {
      const resp = await supportSessionApi.end(session.sessionId, {});
      setSession({
        ...session,
        status: resp?.status || 'ENDED',
      });
      loadHistory();
    } finally {
      setBusy(false);
    }
  }

  async function handleRecontact() {
    setBusy(true);
    try {
      const started = await startOrResumeSupportSession({ category: 'GENERAL' });
      setSession(started);
      setSelectedHistoryId(null);
      const nextCid = String(started.conversationId || cid);
      dispatch({
        type: 'UPSERT_CONVERSATION',
        conversation: {
          conversationId: nextCid,
          id: nextCid,
          conversationType: 'CUSTOMER_SERVICE',
          title: '線上客服',
        },
      });
      await openConversation(nextCid);
      loadHistory();
    } finally {
      setBusy(false);
    }
  }

  async function handleSelectHistory(item) {
    if (!item?.sessionId) return;
    setSelectedHistoryId(item.sessionId);
    setHistoryError('');
    try {
      const detail = await supportSessionApi.getSession(String(item.sessionId));
      if (detail?.conversationId != null) {
        const nextCid = String(detail.conversationId);
        dispatch({
          type: 'UPSERT_CONVERSATION',
          conversation: {
            conversationId: nextCid,
            id: nextCid,
            conversationType: 'CUSTOMER_SERVICE',
            title: '線上客服',
          },
        });
        await openConversation(nextCid);
      }
    } catch (err) {
      setHistoryError(err?.message || '無法開啟歷史');
    }
  }

  async function handleClose() {
    const snap = sessionRef.current;
    if (support && snap?.status === 'WAITING' && snap?.sessionId) {
      setBusy(true);
      try {
        await abandonWaitingSupportSession(snap, 'CLIENT_EXPLICIT_CLOSE');
      } catch {
        /* still close UI */
      } finally {
        setBusy(false);
      }
    }
    closeMiniChat(cid);
  }

  if (minimized) {
    return (
      <button
        type="button"
        className="mini-chat-pill"
        onClick={() => restoreMiniChat(cid)}
        title={title}
      >
        <span className="mini-chat-pill__title">{title}</span>
        {shouldAlertUnread(conv) ? (
          <span className="mini-chat-badge">{unread > 99 ? '99+' : unread}</span>
        ) : null}
      </button>
    );
  }

  return (
    <div
      className={`mini-chat-window${support ? ' mini-chat-window--support' : ''}`}
      data-socket={state.socketStatus}
    >
      <header className="mini-chat-window__header">
        <div className="mini-chat-window__titles">
          <strong>
            {title}
            {normalizeMuteMode(conv) === 'ALL'
              ? ' ·全靜音'
              : normalizeMuteMode(conv) === 'EXCEPT_MENTION'
                ? ' ·靜音·@'
                : ''}
          </strong>
          <small data-status={state.socketStatus}>{socketLabel}</small>
        </div>
        {shouldAlertUnread(conv) ? (
          <span className="mini-chat-badge">{unread > 99 ? '99+' : unread}</span>
        ) : null}
        <div className="mini-chat-window__actions">
          {!support ? <ConversationMuteMenu conversationId={cid} conversation={conv} /> : null}
          {!support && conv?.peerMemberId != null ? (
            <Link
              to={`/chat/report?memberId=${encodeURIComponent(conv.peerMemberId)}&conversationId=${encodeURIComponent(cid)}`}
              className="mini-chat-window__link"
              title="檢舉"
            >
              檢舉
            </Link>
          ) : null}
          <button
            type="button"
            onClick={() => {
              closeMiniChat(cid);
              navigate(support ? '/chat/support' : `/chat/${cid}`);
            }}
            title="展開完整聊天"
          >
            ↗
          </button>
          <button type="button" onClick={() => minimizeMiniChat(cid)} title="最小化">
            —
          </button>
          <button type="button" onClick={handleClose} title="關閉" disabled={busy}>
            ×
          </button>
        </div>
      </header>
      {support ? (
        <>
          <SupportSessionStatusBar
            session={session}
            busy={busy}
            onLeave={handleLeave}
            onEnd={handleEnd}
            onRecontact={handleRecontact}
          />
          <SupportSessionHistoryPanel
            compact
            items={historyItems}
            loading={historyLoading}
            error={historyError}
            open={historyOpen}
            onToggle={() => {
              const next = !historyOpen;
              setHistoryOpen(next);
              if (next) loadHistory();
            }}
            selectedSessionId={selectedHistoryId}
            onSelect={handleSelectHistory}
          />
        </>
      ) : null}
      <div className="mini-chat-window__body">
        <PinnedMessagesBar conversationId={cid} />
        <MessageTimeline conversationId={cid} />
      </div>
      <TypingIndicator conversationId={cid} />
      <div className="mini-chat-window__composer">
        {allowComposer ? (
          <Composer conversationId={cid} />
        ) : (
          <div className="support-composer-fallback">
            {isTerminalSupportStatus(session?.status) ? (
              <button type="button" className="chat-link-btn" disabled={busy} onClick={handleRecontact}>
                再次聯絡
              </button>
            ) : (
              <span>目前無法傳送訊息</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
